import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clipboard,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  XCircle
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  createAdminApiKey,
  listAdminApiKeys,
  revokeAdminApiKey,
  type AdminApiKey,
  type CreatedAdminApiKey
} from '../lib/api/apiKeys'
import { cn } from '../utils/cn'

const DEFAULT_EXPIRY_DAYS = '90'

function formatDate(value?: string | null, fallback = 'Never') {
  if (!value) return fallback

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value))
}

function getKeyStatus(key: AdminApiKey) {
  if (key.revokedAt) {
    return {
      label: 'Revoked',
      icon: XCircle,
      className: 'border-red-200 bg-red-50 text-red-700'
    }
  }

  if (!key.isActive) {
    return {
      label: 'Expired',
      icon: AlertTriangle,
      className: 'border-amber-200 bg-amber-50 text-amber-800'
    }
  }

  return {
    label: 'Active',
    icon: ShieldCheck,
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }
}

function buildExpiryIso(daysText: string) {
  const trimmed = daysText.trim()
  if (!trimmed) {
    return null
  }

  if (!/^\d+$/.test(trimmed)) {
    throw new Error('Expiry must be a positive number of days.')
  }

  const days = Number.parseInt(trimmed, 10)
  if (!Number.isFinite(days) || days <= 0) {
    throw new Error('Expiry must be a positive number of days.')
  }

  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
}

export const ApiKeysPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [keys, setKeys] = useState<AdminApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [expiryDays, setExpiryDays] = useState(DEFAULT_EXPIRY_DAYS)
  const [createdKey, setCreatedKey] = useState<CreatedAdminApiKey | null>(null)

  const isAdmin = String(user?.role ?? 'Guest') === 'Admin'

  const hydrate = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      setKeys(await listAdminApiKeys())
    } catch (error) {
      console.error('Failed to load API keys', error)
      addToast({
        title: 'Could not load API keys',
        description: 'Please refresh or try again later.',
        variant: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast, isAdmin])

  useEffect(() => {
    void hydrate()
  }, [hydrate])

  const keyCounts = useMemo(() => {
    return keys.reduce(
      (counts, key) => {
        if (key.isActive) counts.active += 1
        if (key.revokedAt) counts.revoked += 1
        if (!key.isActive && !key.revokedAt) counts.expired += 1
        return counts
      },
      { active: 0, revoked: 0, expired: 0 }
    )
  }, [keys])

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName) {
      addToast({
        title: 'Name is required',
        variant: 'error'
      })
      return
    }

    let expiresAt: string | null
    try {
      expiresAt = buildExpiryIso(expiryDays)
    } catch (error) {
      addToast({
        title: 'Invalid expiry',
        description: error instanceof Error ? error.message : 'Use a positive number of days.',
        variant: 'error'
      })
      return
    }

    setCreating(true)
    try {
      const created = await createAdminApiKey({ name: trimmedName, expiresAt })
      setCreatedKey(created)
      setKeys((current) => [created, ...current])
      setName('')
      setExpiryDays(DEFAULT_EXPIRY_DAYS)
      addToast({
        title: 'API key created',
        description: 'Copy the key before leaving this page.',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to create API key', error)
      addToast({
        title: 'Could not create API key',
        description: 'Please try again later.',
        variant: 'error'
      })
    } finally {
      setCreating(false)
    }
  }

  const handleCopyCreatedKey = async () => {
    if (!createdKey) return

    try {
      await navigator.clipboard.writeText(createdKey.key)
      addToast({
        title: 'Copied',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to copy API key', error)
      addToast({
        title: 'Copy failed',
        description: 'Select the key text and copy it manually.',
        variant: 'error'
      })
    }
  }

  const handleRevoke = async (key: AdminApiKey) => {
    const confirmed = window.confirm(`Revoke "${key.name}"? This cannot be undone.`)
    if (!confirmed) return

    setRevokingId(key.id)
    try {
      await revokeAdminApiKey(key.id)
      const revokedAt = new Date().toISOString()
      setKeys((current) =>
        current.map((item) =>
          item.id === key.id
            ? { ...item, isActive: false, revokedAt }
            : item
        )
      )
      if (createdKey?.id === key.id) {
        setCreatedKey(null)
      }
      addToast({
        title: 'API key revoked',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to revoke API key', error)
      addToast({
        title: 'Could not revoke API key',
        description: 'Please try again later.',
        variant: 'error'
      })
    } finally {
      setRevokingId(null)
    }
  }

  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">API Keys</h1>
          <p className="mt-1 text-sm text-muted-foreground">Admin access is required.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">API Keys</h1>
          <p className="mt-1 text-sm text-muted-foreground">Admin automation access for dashboard-backed tools.</p>
        </div>
        <Button variant="outline" onClick={hydrate} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-background px-4 py-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Active</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{keyCounts.active}</p>
        </div>
        <div className="rounded-lg border border-border bg-background px-4 py-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Revoked</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{keyCounts.revoked}</p>
        </div>
        <div className="rounded-lg border border-border bg-background px-4 py-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Expired</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{keyCounts.expired}</p>
        </div>
      </div>

      <section className="rounded-lg border border-border bg-card p-4">
        <form className="grid gap-4 lg:grid-cols-[1fr_180px_auto]" onSubmit={handleCreate}>
          <label className="grid gap-1.5 text-sm font-medium text-foreground">
            Key name
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="CLI string table updater"
              maxLength={120}
              disabled={creating}
            />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-foreground">
            Expires in days
            <Input
              value={expiryDays}
              onChange={(event) => setExpiryDays(event.target.value)}
              inputMode="numeric"
              placeholder="90"
              disabled={creating}
            />
          </label>
          <div className="flex items-end">
            <Button type="submit" disabled={creating} className="w-full lg:w-auto">
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Create Key
            </Button>
          </div>
        </form>
      </section>

      {createdKey && (
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <h2 className="text-base font-semibold">New key ready</h2>
              </div>
              <p className="text-sm text-amber-900">Shown once. Copy it before leaving this page.</p>
              <code className="block max-w-full overflow-x-auto rounded-md border border-amber-200 bg-white px-3 py-2 font-mono text-xs text-foreground">
                {createdKey.key}
              </code>
            </div>
            <Button type="button" variant="outline" onClick={handleCopyCreatedKey}>
              <Clipboard className="mr-2 h-4 w-4" />
              Copy
            </Button>
          </div>
        </section>
      )}

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Issued Keys</h2>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading API keys
          </div>
        ) : keys.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-background px-4 py-8 text-center">
            <KeyRound className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-semibold text-foreground">No API keys yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Create the first key for CLI access.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {keys.map((key) => {
              const status = getKeyStatus(key)
              const StatusIcon = status.icon
              const revoking = revokingId === key.id

              return (
                <article key={key.id} className="rounded-lg border border-border bg-card p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-foreground">{key.name}</h3>
                        <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold', status.className)}>
                          <StatusIcon className="h-3.5 w-3.5" />
                          {status.label}
                        </span>
                      </div>
                      <div className="grid gap-3 text-sm text-muted-foreground md:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <p className="font-medium text-foreground">Prefix</p>
                          <p className="mt-1 font-mono text-xs">{key.keyPrefix}</p>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Created</p>
                          <p className="mt-1">{formatDate(key.createdAt)}</p>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Last Used</p>
                          <p className="mt-1">{formatDate(key.lastUsedAt, 'Not used yet')}</p>
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Expires</p>
                          <p className="mt-1">{formatDate(key.expiresAt, 'No expiry')}</p>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => handleRevoke(key)}
                      disabled={Boolean(key.revokedAt) || revoking}
                      className="w-full lg:w-auto"
                    >
                      {revoking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                      Revoke
                    </Button>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
